/**
 * useAppSettingPatch -- admin app_settings PATCH mutation (P43.2).
 *
 * Calls PATCH /v1/admin/settings/app/{key} with a typed value.
 * Backend validates against the row's data_type and writes both an
 * audit row and a per-key hash-chain snapshot. Cache invalidates
 * the admin-settings query on success.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { api } from "shared/api/client";
import type { SettingsAppRow } from "shared/api/types";

interface PatchInput {
  key: string;
  value: unknown;
}

export function useAppSettingPatch(): UseMutationResult<
  SettingsAppRow,
  Error,
  PatchInput
> {
  const qc = useQueryClient();
  return useMutation<SettingsAppRow, Error, PatchInput>({
    mutationFn: ({ key, value }: PatchInput) =>
      api.patch<SettingsAppRow>(`/v1/admin/settings/app/${encodeURIComponent(key)}`, {
        value,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin-settings"] });
      // P44.2: also refresh any history timeline open for this key.
      void qc.invalidateQueries({
        queryKey: ["admin-settings-history", vars.key],
      });
    },
  });
}
